import { useAuth } from '@/contexts/AuthContext';
import bedrockAgentCoreService from '@/services/bedrockAgentCoreService';
import type { DemoData } from '@demo-portal/shared';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Add CSS animations
const animationStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = animationStyles;
  document.head.appendChild(style);
}

interface PromptDemoGeneratorProps { }

interface PromptDemoGeneratorState {
  prompt: string;
  isGenerating: boolean;
  error: string | null;
  validationErrors: string[];
  generationProgress: {
    step: string;
    message: string;
    progress: number;
  } | null;
  retryCount: number;
}

const PromptDemoGenerator: React.FC<PromptDemoGeneratorProps> = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState<PromptDemoGeneratorState>({
    prompt: '',
    isGenerating: false,
    error: null,
    validationErrors: [],
    generationProgress: null,
    retryCount: 0,
  });

  // Example prompts to guide user input
  const examplePrompts = [
    {
      category: 'Automotive',
      text: 'Automotive parts certification for ISO compliance and quality assurance processes',
      icon: '🚗'
    },
    {
      category: 'Healthcare',
      text: 'Healthcare patient diagnosis and treatment planning with compliance monitoring',
      icon: '🏥'
    },
    {
      category: 'Finance',
      text: 'Financial risk assessment for loan approvals and regulatory compliance',
      icon: '💰'
    },
    {
      category: 'Manufacturing',
      text: 'Fragrance development and manufacturing quality control for cosmetics industry',
      icon: '🌸'
    },
    {
      category: 'Retail',
      text: 'Supply chain optimization and inventory management for e-commerce platforms',
      icon: '🛍️'
    },
    {
      category: 'Technology',
      text: 'Software development lifecycle management with automated testing and deployment',
      icon: '💻'
    }
  ];

  // Validate prompt input
  const validatePrompt = (prompt: string): string[] => {
    const errors: string[] = [];

    if (!prompt.trim()) {
      errors.push('Please enter a business use case description');
    } else if (prompt.trim().length < 10) {
      errors.push('Please provide a more detailed description (at least 10 characters)');
    } else if (prompt.trim().length > 1000) {
      errors.push('Please keep your description under 1000 characters');
    }

    // Check for meaningful content (not just spaces or repeated characters)
    const trimmedPrompt = prompt.trim();
    if (trimmedPrompt && trimmedPrompt.length >= 10) {
      const uniqueChars = new Set(trimmedPrompt.toLowerCase().replace(/\s/g, '')).size;
      if (uniqueChars < 5) {
        errors.push('Please provide a more meaningful business use case description');
      }
    }

    return errors;
  };

  // Handle prompt input change
  const handlePromptChange = (value: string) => {
    setState(prev => ({
      ...prev,
      prompt: value,
      validationErrors: value ? validatePrompt(value) : [],
      error: null
    }));
  };

  // Update generation progress
  const updateProgress = (step: string, message: string, progress: number) => {
    setState(prev => ({
      ...prev,
      generationProgress: { step, message, progress }
    }));
  };

  // Handle form submission with progress tracking
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await generateDemo();
  };

  // Generate demo with progress tracking and retry logic
  const generateDemo = async () => {
    const validationErrors = validatePrompt(state.prompt);
    if (validationErrors.length > 0) {
      setState(prev => ({ ...prev, validationErrors }));
      return;
    }

    setState(prev => ({
      ...prev,
      isGenerating: true,
      error: null,
      validationErrors: [],
      generationProgress: null
    }));

    try {
      // Step 1: Initialize
      updateProgress('initialize', 'Initializing demo generation...', 10);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 2: Analyze prompt
      updateProgress('analyze', 'Analyzing business use case...', 25);
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 3: Generate configuration with streaming
      updateProgress('generate', 'Generating demo configuration...', 50);

      // Generate demo configuration using Bedrock Agent Core service (direct call)
      const demoData: DemoData = await bedrockAgentCoreService.generateDemoFromPrompt(state.prompt);

      updateProgress('generate', 'Configuration generated successfully!', 70);

      // Step 4: Validate configuration
      updateProgress('validate', 'Validating configuration...', 75);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 5: Prepare demo
      updateProgress('prepare', 'Preparing demo viewer...', 90);

      // Store temporary configuration and navigate to demo viewer
      const tempId = storeTempDemo(demoData);

      updateProgress('complete', 'Demo ready!', 100);
      await new Promise(resolve => setTimeout(resolve, 500));

      navigate(`/demo/temp-${tempId}`);

    } catch (error) {
      console.error('Demo generation failed:', error);

      let errorMessage = 'Failed to generate demo configuration';
      let canRetry = true;

      if (error instanceof Error) {
        errorMessage = error.message;

        // Determine if error is retryable
        if (error.message.includes('Token provider not configured') ||
          error.message.includes('No JWT token available')) {
          canRetry = false;
          errorMessage = 'Authentication error. Please refresh the page and try again.';
        } else if (error.message.includes('Invalid JSON response') ||
          error.message.includes('Response validation failed')) {
          errorMessage = 'The AI generated an invalid response. Please try again with a different prompt.';
        } else if (error.message.includes('Failed to generate demo configuration')) {
          errorMessage = 'The demo generation service is temporarily unavailable. Please try again.';
        }
      }

      setState(prev => ({
        ...prev,
        isGenerating: false,
        error: errorMessage,
        generationProgress: null,
        retryCount: canRetry ? prev.retryCount + 1 : prev.retryCount
      }));
    }
  };

  // Handle retry
  const handleRetry = () => {
    generateDemo();
  };

  // Store temporary demo configuration in session storage
  const storeTempDemo = (demoData: DemoData): string => {
    const tempId = `${Date.now()}`;
    const storageData = {
      id: tempId,
      ...demoData,
      createdAt: Date.now(),
      expiresAt: Date.now() + (60 * 60 * 1000), // 1 hour expiry
    };

    sessionStorage.setItem(`temp-demo-${tempId}`, JSON.stringify(storageData));
    return tempId;
  };

  // Handle clear form
  const handleClear = () => {
    setState({
      prompt: '',
      isGenerating: false,
      error: null,
      validationErrors: [],
      generationProgress: null,
      retryCount: 0,
    });
  };

  // Handle example prompt selection
  const handleExampleSelect = (exampleText: string) => {
    handlePromptChange(exampleText);
  };

  // Character count for input guidelines
  const characterCount = state.prompt.length;
  const maxCharacters = 1000;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '40px',
          color: 'white'
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
            }}
          >
            ← Back to Portal
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ opacity: 0.9 }}>Welcome, {user?.username || user?.email}</span>
            <button
              onClick={signOut}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '40px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }}>
          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h1 style={{
              fontSize: '2.5em',
              marginBottom: '16px',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              ⚡ Quick Demo Generator
            </h1>
            <p style={{
              fontSize: '1.2em',
              color: '#666',
              lineHeight: '1.6',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Generate a complete multi-agent demo from your business use case description.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ marginBottom: '40px' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '1.1em',
                fontWeight: '600',
                color: '#2c3e50',
                marginBottom: '12px'
              }}>
                Describe your business use case:
              </label>

              <textarea
                value={state.prompt}
                onChange={(e) => handlePromptChange(e.target.value)}
                placeholder="Enter a detailed description of your business use case, industry, and the type of multi-agent system you'd like to demonstrate..."
                disabled={state.isGenerating}
                style={{
                  width: '100%',
                  minHeight: '150px',
                  padding: '16px',
                  border: state.validationErrors.length > 0 ? '2px solid #ef4444' : '2px solid #e1e8ed',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: state.isGenerating ? '#f9fafb' : 'white',
                  cursor: state.isGenerating ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => {
                  if (!state.isGenerating && state.validationErrors.length === 0) {
                    e.target.style.borderColor = '#667eea';
                  }
                }}
                onBlur={(e) => {
                  if (state.validationErrors.length === 0) {
                    e.target.style.borderColor = '#e1e8ed';
                  }
                }}
              />

              {/* Character count */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '8px',
                fontSize: '14px',
                color: '#666'
              }}>
                <div>
                  {state.validationErrors.length > 0 && (
                    <span style={{ color: '#ef4444' }}>
                      {state.validationErrors[0]}
                    </span>
                  )}
                </div>
                <div style={{
                  color: characterCount > maxCharacters * 0.9 ? '#ef4444' : '#666'
                }}>
                  {characterCount}/{maxCharacters} characters
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleClear}
                disabled={state.isGenerating || !state.prompt}
                style={{
                  padding: '14px 28px',
                  background: 'transparent',
                  border: '2px solid #e1e8ed',
                  color: '#666',
                  borderRadius: '8px',
                  cursor: (state.isGenerating || !state.prompt) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  opacity: (state.isGenerating || !state.prompt) ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (!state.isGenerating && state.prompt) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.color = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!state.isGenerating && state.prompt) {
                    e.currentTarget.style.borderColor = '#e1e8ed';
                    e.currentTarget.style.color = '#666';
                  }
                }}
              >
                Clear
              </button>

              <button
                type="submit"
                disabled={state.isGenerating || state.validationErrors.length > 0 || !state.prompt.trim()}
                style={{
                  padding: '14px 28px',
                  background: (state.isGenerating || state.validationErrors.length > 0 || !state.prompt.trim())
                    ? '#9ca3af'
                    : 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (state.isGenerating || state.validationErrors.length > 0 || !state.prompt.trim())
                    ? 'not-allowed'
                    : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: (state.isGenerating || state.validationErrors.length > 0 || !state.prompt.trim())
                    ? 'none'
                    : '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '180px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!state.isGenerating && state.validationErrors.length === 0 && state.prompt.trim()) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!state.isGenerating && state.validationErrors.length === 0 && state.prompt.trim()) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }
                }}
              >
                {state.isGenerating ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    {state.generationProgress ?
                      `${state.generationProgress.progress}% Complete` :
                      'Generating...'
                    }
                  </>
                ) : (
                  <>
                    🚀 Generate Demo
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Loading State with Progress */}
          {state.isGenerating && state.generationProgress && (
            <div style={{
              padding: '24px',
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '12px',
              marginBottom: '24px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #e0e7ff',
                  borderTop: '3px solid #667eea',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div>
                  <div style={{
                    fontSize: '1.1em',
                    fontWeight: '600',
                    color: '#1e40af',
                    marginBottom: '4px'
                  }}>
                    🤖 Generating Your Demo...
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#3730a3'
                  }}>
                    {state.generationProgress.message}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e0e7ff',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '12px'
              }}>
                <div style={{
                  width: `${state.generationProgress.progress}%`,
                  height: '100%',
                  backgroundColor: '#667eea',
                  borderRadius: '4px',
                  transition: 'width 0.5s ease-in-out'
                }} />
              </div>

              {/* Progress Steps */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#6366f1'
              }}>
                <span style={{
                  opacity: state.generationProgress.progress >= 10 ? 1 : 0.5,
                  fontWeight: state.generationProgress.step === 'initialize' ? '600' : 'normal'
                }}>
                  ✓ Initialize
                </span>
                <span style={{
                  opacity: state.generationProgress.progress >= 25 ? 1 : 0.5,
                  fontWeight: state.generationProgress.step === 'analyze' ? '600' : 'normal'
                }}>
                  ✓ Analyze Use Case
                </span>
                <span style={{
                  opacity: state.generationProgress.progress >= 50 ? 1 : 0.5,
                  fontWeight: state.generationProgress.step === 'generate' ? '600' : 'normal'
                }}>
                  ⏳ Generate Config
                </span>
                <span style={{
                  opacity: state.generationProgress.progress >= 75 ? 1 : 0.5,
                  fontWeight: state.generationProgress.step === 'validate' ? '600' : 'normal'
                }}>
                  ⏳ Validate
                </span>
                <span style={{
                  opacity: state.generationProgress.progress >= 90 ? 1 : 0.5,
                  fontWeight: state.generationProgress.step === 'prepare' ? '600' : 'normal'
                }}>
                  ⏳ Prepare Demo
                </span>
              </div>
            </div>
          )}

          {/* Error Display */}
          {state.error && (
            <div style={{
              padding: '20px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              color: '#dc2626',
              marginBottom: '24px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <span style={{ fontSize: '24px', flexShrink: 0 }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1.1em' }}>
                    Generation Failed
                  </div>
                  <div style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '12px' }}>
                    {state.error}
                  </div>

                  {/* Retry attempts info */}
                  {state.retryCount > 0 && (
                    <div style={{
                      fontSize: '12px',
                      opacity: 0.8,
                      marginBottom: '12px',
                      fontStyle: 'italic'
                    }}>
                      Attempt {state.retryCount + 1} failed
                    </div>
                  )}
                </div>
              </div>

              {/* Recovery options */}
              <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                {/* Retry button - show if retries are reasonable */}
                {state.retryCount < 3 && !state.error.includes('Authentication error') && (
                  <button
                    onClick={handleRetry}
                    disabled={state.isGenerating}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: state.isGenerating ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: state.isGenerating ? 0.5 : 1,
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      if (!state.isGenerating) {
                        e.currentTarget.style.backgroundColor = '#b91c1c';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!state.isGenerating) {
                        e.currentTarget.style.backgroundColor = '#dc2626';
                      }
                    }}
                  >
                    🔄 Retry Generation
                  </button>
                )}

                {/* Clear and start over */}
                <button
                  onClick={handleClear}
                  disabled={state.isGenerating}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#dc2626',
                    border: '1px solid #dc2626',
                    borderRadius: '6px',
                    cursor: state.isGenerating ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: state.isGenerating ? 0.5 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!state.isGenerating) {
                      e.currentTarget.style.backgroundColor = '#dc2626';
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!state.isGenerating) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#dc2626';
                    }
                  }}
                >
                  ✨ Start Over
                </button>

                {/* Go back to portal */}
                <button
                  onClick={() => navigate('/')}
                  disabled={state.isGenerating}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: state.isGenerating ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: state.isGenerating ? 0.5 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!state.isGenerating) {
                      e.currentTarget.style.backgroundColor = '#6b7280';
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!state.isGenerating) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#6b7280';
                    }
                  }}
                >
                  ← Back to Portal
                </button>
              </div>

              {/* Help text for persistent errors */}
              {state.retryCount >= 2 && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#92400e'
                }}>
                  <strong>Still having trouble?</strong> Try using the conversational demo creator instead,
                  or simplify your prompt to focus on a specific industry or use case.
                </div>
              )}
            </div>
          )}

          {/* Example Prompts */}
          <div>
            <h3 style={{
              fontSize: '1.3em',
              fontWeight: '600',
              color: '#2c3e50',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              💡 Example Prompts
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '16px'
            }}>
              {examplePrompts.map((example, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleExampleSelect(example.text)}
                  disabled={state.isGenerating}
                  style={{
                    padding: '16px',
                    background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    cursor: state.isGenerating ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    opacity: state.isGenerating ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!state.isGenerating) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      e.currentTarget.style.borderColor = '#667eea';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!state.isGenerating) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '24px', flexShrink: 0 }}>{example.icon}</span>
                    <div>
                      <div style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginBottom: '6px'
                      }}>
                        {example.category}
                      </div>
                      <div style={{
                        color: '#666',
                        fontSize: '14px',
                        lineHeight: '1.4'
                      }}>
                        {example.text}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default PromptDemoGenerator;